import { RadioUniDriver, ToggleSwitchUniDriver } from '@wix/design-system/dist/testkit/unidriver';
import type { GetOptions, Testkit, UniDriver } from 'ricos-types/testkit';
import { dataHook, RicosPluginToolbarNames } from 'wix-rich-content-common';
import { INSERT_PLUGIN_BUTTONS } from 'wix-rich-content-editor-common';
import type { FooterActionsUnidrver } from 'wix-rich-content-ui-components/testkit';
import { createFooterActionsUnidriver } from 'wix-rich-content-ui-components/testkit';

import {
  COLLAPSIBLE_LIST_EXPANDED_DEFAULT,
  COLLAPSIBLE_LIST_ITEM,
  COLLAPSIBLE_LIST_ONE_PAIR_EXPANDED,
  MODAL_ROOT,
  NEW_PAIR_BUTTON,
  ROOT,
  TOOLBAR_DELETE_BUTTON,
} from '../dataHooks';
import { toolbarsToAddIn } from '../getAddButtons';
import type { CollapsibleListItemUniDriver } from './collapsible-list-item.uni.driver';
import { createCollapsibleListItemUniDriver } from './collapsible-list-item.uni.driver';

export type CollapsibleListTestkit = Testkit<{
  content: {
    atIndex: (index: number) => {
      exists: (options?: GetOptions) => Promise<boolean>;
      getItemsCount: () => Promise<number>;
      getExpandedListItemsCount: () => Promise<number>;
      addNewItem: () => Promise<void>;
      addNewItemButtonExists: () => Promise<boolean>;
      listItem: CollapsibleListItemUniDriver;
    };
  };
  modal: {
    exists: () => Promise<boolean>;
    initialExpandedItems: {
      allOption: ReturnType<typeof RadioUniDriver>;
      firstOption: ReturnType<typeof RadioUniDriver>;
      noneOption: ReturnType<typeof RadioUniDriver>;
    };
    expandOnlyOneToggle: ReturnType<typeof ToggleSwitchUniDriver>;
  } & FooterActionsUnidrver;
  toolbars: {
    createAddButtonToolbars: (getAddButtonToolbarBase: () => UniDriver<Element>) => {
      click: () => Promise<void>;
      toolbarsToAddIn: typeof toolbarsToAddIn;
    };
    createPluginToolbar: (
      getPluginToolbarBase: (
        pluginType: typeof RicosPluginToolbarNames.COLLAPSIBLE_LIST
      ) => UniDriver<Element>
    ) => {
      clickEdit: () => Promise<void>;
      clickDelete: () => Promise<void>;
      exists: () => Promise<boolean>;
    };
  };
}>;

export const createCollapsibleListTestkit: CollapsibleListTestkit = getBase => {
  const getCollapsibleList = async (index: number, options?: GetOptions) => {
    const collapsibleList = await getBase().getAll(dataHook(ROOT), options);
    return collapsibleList.at(index);
  };
  const getSettingsModal = async () => await getBase().get(dataHook(MODAL_ROOT));

  const getModalBase = () => getBase().$(dataHook(MODAL_ROOT));

  return {
    content: {
      atIndex: (index: number) => {
        return {
          exists: async options => {
            try {
              const collapsibleList = await getCollapsibleList(index, options);
              return collapsibleList.exists();
            } catch (error) {
              return false;
            }
          },
          getItemsCount: async () => {
            const collapsibleList = await getCollapsibleList(index);
            const listItems = await collapsibleList.getAll(dataHook(COLLAPSIBLE_LIST_ITEM));
            return listItems.count();
          },
          getExpandedListItemsCount: async () => {
            const collapsibleList = await getCollapsibleList(index);
            const listItems = await collapsibleList.$$(
              dataHook([COLLAPSIBLE_LIST_ITEM, 'expanded'])
            );
            return listItems.count();
          },
          addNewItem: async () => {
            const collapsibleList = await getCollapsibleList(index);
            const addNewItemButton = await collapsibleList.get(dataHook(NEW_PAIR_BUTTON));
            await addNewItemButton.click();
          },
          addNewItemButtonExists: async () => {
            try {
              const collapsibleList = await getCollapsibleList(index);
              const button = await collapsibleList.get(dataHook(NEW_PAIR_BUTTON));
              return button.exists();
            } catch (error) {
              return false;
            }
          },
          listItem: createCollapsibleListItemUniDriver(getBase),
        };
      },
    },
    toolbar: {},
    modal: {
      exists: async () => {
        try {
          const settingsModal = await getSettingsModal();
          return settingsModal.exists();
        } catch (error) {
          return false;
        }
      },
      initialExpandedItems: {
        allOption: RadioUniDriver(
          getModalBase().$(dataHook(COLLAPSIBLE_LIST_EXPANDED_DEFAULT.ALL)),
          getBase()
        ),
        firstOption: RadioUniDriver(
          getModalBase().$(dataHook(COLLAPSIBLE_LIST_EXPANDED_DEFAULT.FIRST)),
          getBase()
        ),
        noneOption: RadioUniDriver(
          getModalBase().$(dataHook(COLLAPSIBLE_LIST_EXPANDED_DEFAULT.NONE)),
          getBase()
        ),
      },
      expandOnlyOneToggle: ToggleSwitchUniDriver(
        getModalBase().$(dataHook(COLLAPSIBLE_LIST_ONE_PAIR_EXPANDED)),
        getBase()
      ),
      ...createFooterActionsUnidriver(getBase),
    },
    toolbars: {
      createAddButtonToolbars: getAddButtonToolbarBase => ({
        click: async () => {
          const collapsibleListButton = await getAddButtonToolbarBase().get(
            dataHook(INSERT_PLUGIN_BUTTONS.COLLAPSIBLE_LIST)
          );
          await collapsibleListButton.click();
        },
        toolbarsToAddIn,
      }),
      createPluginToolbar: getPluginToolbarBase => ({
        clickEdit: async () => {
          const editButton = await getPluginToolbarBase(
            RicosPluginToolbarNames.COLLAPSIBLE_LIST
          ).get(dataHook('baseToolbarButton_settings'), {
            timeout: 5000,
          });
          return editButton.click();
        },
        clickDelete: async () => {
          const deleteButton = await getPluginToolbarBase(
            RicosPluginToolbarNames.COLLAPSIBLE_LIST
          ).get(dataHook(TOOLBAR_DELETE_BUTTON), {
            timeout: 5000,
          });
          return deleteButton.click();
        },
        exists: async () => {
          try {
            const pluginToolbar = await getPluginToolbarBase(
              RicosPluginToolbarNames.COLLAPSIBLE_LIST
            );
            return pluginToolbar.exists();
          } catch (error) {
            return false;
          }
        },
      }),
    },
  };
};
